// ============================================================
// Collect Fee Page
// ============================================================
// Unified Student Directory for fee collection and fee status.
// "Collect" navigates to the fee collection page.
// "View" navigates to the student fee status detail page.

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport, GridToolbarColumnsButton, GridToolbarFilterButton } from '@mui/x-data-grid';
import { Search, IndianRupee } from 'lucide-react';
import { FcBusinessman, FcBusinesswoman } from 'react-icons/fc';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useSchool } from '@/hooks/useSchool';
import { useDebounce } from '@/hooks/useDebounce';
import { studentsApi } from '@/api/students.api';
import { academicSessionsApi } from '@/api/academicSessions.api';

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1, borderBottom: `1px solid ${tokens.colors.border}`, gap: 1 }}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
    </GridToolbarContainer>
  );
}

export function CollectFeePage() {
  usePageMeta('Collect Fee', [
    { label: 'Fees', path: '/fees' },
    { label: 'Collect' },
  ]);

  const navigate = useNavigate();
  const { schoolId } = useSchool();

  // ── Master Reference Data ───────────────────────────────────
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);

  // ── Filter States ───────────────────────────────────────────
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [feeAssignmentFilter, setFeeAssignmentFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // ── Student Data ────────────────────────────────────────────
  const [students, setStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });

  // ── Load Master Data ────────────────────────────────────────
  useEffect(() => {
    if (schoolId) {
      academicSessionsApi
        .getAllBySchool(schoolId)
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.data || [];
          setSessions(list);
          const current = list.find((s) => s.is_current);
          if (current) setSelectedYear(current.id);
        })
        .catch((err) => console.error('Failed to load academic sessions:', err));

      studentsApi
        .getClassesSections()
        .then((res) => {
          const list = res?.data || (Array.isArray(res) ? res : []);
          setClasses(list);
        })
        .catch((err) => console.error('Failed to load classes & sections:', err));
    }
  }, [schoolId]);

  // ── Update Sections when Class changes ──────────────────────
  useEffect(() => {
    if (selectedClass && classes.length > 0) {
      const cls = classes.find((c) => (c.id || c.class_id) === selectedClass);
      setAvailableSections(cls?.sections || []);
    } else {
      setAvailableSections([]);
    }
  }, [selectedClass, classes]);

  // ── Fetch Students ──────────────────────────────────────────
  const fetchStudents = useCallback(
    async (currentPage = 1, limit = 20) => {
      if (!schoolId || !selectedYear) return;
      setIsLoadingStudents(true);
      try {
        const params = { academic_session_id: selectedYear };
        if (selectedClass) params.class_id = selectedClass;
        if (selectedSection) params.section_id = selectedSection;
        if (debouncedSearchTerm) params.search = debouncedSearchTerm;
        if (feeAssignmentFilter === 'ASSIGNED') {
          params.fee_assigned = true;
          params.is_fee_assigned = true;
        } else if (feeAssignmentFilter === 'NOT_ASSIGNED') {
          params.fee_assigned = false;
          params.is_fee_assigned = false;
        }

        const res = await studentsApi.getStudents(schoolId, currentPage, limit, params);
        let items = res?.items || res?.data || (Array.isArray(res) ? res : []);

        // Client-side fallback filtering to guarantee correctness if backend query params differ
        if (feeAssignmentFilter === 'ASSIGNED') {
          items = items.filter((s) => Boolean(s.fee_assignment?.assigned));
        } else if (feeAssignmentFilter === 'NOT_ASSIGNED') {
          items = items.filter((s) => !s.fee_assignment?.assigned);
        }

        setStudents(items);
        setPagination({
          page: res?.page || currentPage,
          total: feeAssignmentFilter ? items.length : (res?.total || items.length),
          total_pages: res?.total_pages || 1,
        });
      } catch (err) {
        console.error('Failed to load students:', err);
      } finally {
        setIsLoadingStudents(false);
      }
    },
    [schoolId, selectedYear, selectedClass, selectedSection, debouncedSearchTerm, feeAssignmentFilter],
  );

  useEffect(() => {
    setPage(1);
  }, [selectedYear, selectedClass, selectedSection, debouncedSearchTerm, feeAssignmentFilter]);

  useEffect(() => {
    fetchStudents(page, pageSize);
  }, [fetchStudents, page, pageSize]);

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
    setSelectedSection('');
  };

  const handleOpenCollectPage = (student) => {
    const id = student.student_id || student.id;
    navigate(`/fees/collect/${id}`);
  };

  const handleViewStatus = (student) => {
    const id = student.student_id || student.id;
    navigate(`/fees/student-status/${id}`);
  };

  // ── Student Directory Columns ───────────────────────────────
  const studentColumns = [
    {
      field: 'student_name',
      headerName: 'Student Name',
      flex: 1.1,
      minWidth: 150,
      valueGetter: (value, row) => (row || value?.row || value)?.student_name || '-',
      renderCell: (params) => {
        const isFemale = String(params?.row?.gender || '').toUpperCase() === 'FEMALE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            {isFemale ? <FcBusinesswoman size={24} /> : <FcBusinessman size={24} />}
            <Typography variant="body2" fontWeight={500} noWrap>
              {params.row?.student_name || params.value || '-'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'admission_no',
      headerName: 'Adm. No.',
      flex: 0.8,
      minWidth: 110,
      valueGetter: (value, row) => (row || value?.row || value)?.admission_no || (row || value?.row || value)?.admission_number || '-',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500} noWrap>
          {params.row?.admission_no || params.row?.admission_number || params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'roll_number',
      headerName: 'Roll No.',
      flex: 0.45,
      minWidth: 65,
      valueGetter: (value, row) => (row || value?.row || value)?.roll_number || '-',
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {params.row?.roll_number || params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'gender',
      headerName: 'Gender',
      flex: 0.5,
      minWidth: 70,
      valueGetter: (value, row) => (row || value?.row || value)?.gender || '-',
      renderCell: (params) => (
        <Typography variant="body2" noWrap sx={{ textTransform: 'capitalize' }}>
          {(params.row?.gender || params.value || '-').toLowerCase()}
        </Typography>
      ),
    },
    {
      field: 'class_name',
      headerName: 'Class',
      flex: 0.5,
      minWidth: 70,
      valueGetter: (value, row) => {
        const r = row || value?.row || value || {};
        return r.class?.name || r.class?.class_name || r.class_name || r.className || '-';
      },
      renderCell: (params) => {
        const r = params.row || {};
        const val = r.class?.name || r.class?.class_name || r.class_name || r.className || params.value || '-';
        return (
          <Typography variant="body2" noWrap>
            {val}
          </Typography>
        );
      },
    },
    {
      field: 'section_name',
      headerName: 'Section',
      flex: 0.4,
      minWidth: 60,
      valueGetter: (value, row) => {
        const r = row || value?.row || value || {};
        return r.section?.name || r.section?.section_name || r.section_name || r.sectionName || '-';
      },
      renderCell: (params) => {
        const r = params.row || {};
        const val = r.section?.name || r.section?.section_name || r.section_name || r.sectionName || params.value || '-';
        return (
          <Typography variant="body2" noWrap>
            {val}
          </Typography>
        );
      },
    },
    {
      field: 'father_name',
      headerName: 'Father Name',
      flex: 0.9,
      minWidth: 120,
      valueGetter: (value, row) => {
        const r = row || value?.row || value || {};
        return r.parent?.father_name || r.father_name || r.parent?.guardian_name || '-';
      },
      renderCell: (params) => {
        const r = params.row || {};
        const val = r.parent?.father_name || r.father_name || r.parent?.guardian_name || params.value || '-';
        return (
          <Typography variant="body2" noWrap>
            {val}
          </Typography>
        );
      },
    },
    {
      field: 'father_mobile',
      headerName: 'Father Mobile',
      flex: 0.8,
      minWidth: 105,
      valueGetter: (value, row) => {
        const r = row || value?.row || value || {};
        return r.parent?.father_mobile || r.father_mobile || r.mobile || r.parent?.guardian_mobile || '-';
      },
      renderCell: (params) => {
        const r = params.row || {};
        const val = r.parent?.father_mobile || r.father_mobile || r.mobile || r.parent?.guardian_mobile || params.value || '-';
        return (
          <Typography variant="body2" noWrap>
            {val}
          </Typography>
        );
      },
    },
    {
      field: 'fee_assignment',
      headerName: 'Fee Assignment',
      flex: 0.9,
      minWidth: 120,
      valueGetter: (value, row) => {
        const r = row || value?.row || value || {};
        return r.fee_assignment?.assigned ? 'Assigned' : 'Not Assigned';
      },
      renderCell: (params) => {
        const r = params.row || {};
        const isAssigned = Boolean(r.fee_assignment?.assigned);
        const structureName = r.fee_assignment?.fee_structure_name;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 0.3 }}>
            <Tooltip
              title={
                isAssigned
                  ? (structureName ? `Structure: ${structureName}` : 'Fee Structure Assigned')
                  : 'Fee structure is not assigned to this student'
              }
            >
              <Chip
                label={isAssigned ? 'Assigned' : 'Not Assigned'}
                size="small"
                color={isAssigned ? 'success' : 'warning'}
                variant={isAssigned ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  textTransform: 'none',
                  ...(isAssigned
                    ? {}
                    : {
                        borderColor: '#f59e0b',
                        color: '#d97706',
                        backgroundColor: '#fef3c7',
                      }),
                }}
              />
            </Tooltip>
            {isAssigned && structureName && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  color: 'text.secondary',
                  lineHeight: 1.2,
                  maxWidth: 130,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {structureName}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 115,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isAssigned = Boolean(params.row?.fee_assignment?.assigned);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Tooltip title={!isAssigned ? 'Fee structure is not assigned' : ''}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!isAssigned}
                  startIcon={<IndianRupee size={13} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isAssigned) handleOpenCollectPage(params.row);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1.5,
                    fontSize: '0.75rem',
                    py: 0.4,
                    px: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Collect Fee
                </Button>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ pb: 6 }}>
      <PageHeader
        title="Collect Fee"
        subtitle="Search students, collect pending fees, and view detailed fee status."
      />

      <WidgetContainer noPadding sx={{ mt: 3 }}>
        {/* Filters Bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${tokens.colors.border}`,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            placeholder="Search by name or admission no..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 260 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color={tokens.colors.text.secondary} />
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Academic Year</InputLabel>
            <Select
              value={selectedYear}
              label="Academic Year"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <MenuItem value="">
                <em>All Years</em>
              </MenuItem>
              {sessions.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.session_name || s.name}
                  {s.is_current ? ' (Current)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Class</InputLabel>
            <Select value={selectedClass} label="Class" onChange={handleClassChange}>
              <MenuItem value="">
                <em>All Classes</em>
              </MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls.id || cls.class_id} value={cls.id || cls.class_id}>
                  {cls.class_name || cls.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Section</InputLabel>
            <Select
              value={selectedSection}
              label="Section"
              onChange={(e) => setSelectedSection(e.target.value)}
              disabled={!selectedClass}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {availableSections.map((sec) => (
                <MenuItem key={sec.id} value={sec.id}>
                  {sec.section_name || sec.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Fee Assignment</InputLabel>
            <Select
              value={feeAssignmentFilter}
              label="Fee Assignment"
              onChange={(e) => setFeeAssignmentFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>All Students</em>
              </MenuItem>
              <MenuItem value="ASSIGNED">Assigned</MenuItem>
              <MenuItem value="NOT_ASSIGNED">Not Assigned</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Student DataGrid */}
        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={students}
            columns={studentColumns}
            loading={isLoadingStudents}
            getRowId={(row) => row.student_id || row.id}
            paginationMode="server"
            rowCount={pagination.total}
            pageSizeOptions={[10, 20, 50, 100]}
            paginationModel={{
              page: page - 1,
              pageSize: pageSize,
            }}
            onPaginationModelChange={(model) => {
              setPage(model.page + 1);
              setPageSize(model.pageSize);
            }}
            disableRowSelectionOnClick
            onRowClick={(params) => handleViewStatus(params.row)}
            slots={{ toolbar: CustomToolbar }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
              '& .MuiDataGrid-row:hover': { backgroundColor: `${tokens.colors.primary.main}08`, cursor: 'pointer' },
            }}
          />
        </Box>
      </WidgetContainer>
    </Box>
  );
}
